import React, { useState, useEffect } from "react";
import { Tooltip } from "@mui/material";
import { confirmDialog } from "../../../utils/confirm";
import { Users as UsersIcon, UserPlus, Pencil, Trash2 } from "lucide-react";
import Button from "../../../components/Button";
import AddUser from "./AddUser";
import {
  getUsers as apiGetUsers,
  deleteUser as apiDeleteUser,
} from "../../../api";
import PageLoader from "../../../components/PageLoader";
import Swal from "sweetalert2";

const AdminUsers = () => {
  const [users, setUsersState] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const loadUsers = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError("");
    try {
      const list = await apiGetUsers();
      setUsersState(list);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load users",
      );
      setUsersState([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleEdit = (u) => {
    setEditingUser(u);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Delete user?",
      text: "This user will be removed.",
    });
    if (!confirmed) return;
    try {
      await apiDeleteUser(id);
      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "User deleted successfully",
        confirmButtonColor: "#38915c",
      });
      await loadUsers(false);
      if (editingUser?.id === id) {
        setEditingUser(null);
        setModalOpen(false);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Failed to delete user";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
        confirmButtonColor: "#38915c",
      });
      setError(msg);
    }
  };

  const openAddModal = () => {
    setEditingUser(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingUser(null);
  };

  const displayFirstName = (u) =>
    u.firstName ?? (u.name ? u.name.split(" ")[0] : "—");
  const displayLastName = (u) =>
    u.lastName ?? (u.name ? u.name.split(" ").slice(1).join(" ") : "—");

  return (
    <div>
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-primary-600 mb-1">
            <UsersIcon className="w-5 h-5" />
            <span className="text-sm font-medium uppercase tracking-wide">
              Manage
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Users</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Registered user accounts
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={openAddModal}
          className="flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" />
          Add User
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-danger-50 text-danger-600 text-sm">
          {error}
        </div>
      )}
      {loading && <PageLoader />}

      <AddUser
        open={modalOpen}
        onClose={closeModal}
        onSuccess={() => loadUsers(false)}
        editUser={editingUser}
      />

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  First name
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Last name
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Email
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Mobile
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  PAN / Broker
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Experience / Risk
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Role
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Registered
                </th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {!loading && users.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-gray-500">
                    No registered users yet. Users will appear here when they
                    sign up.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 px-4">{displayFirstName(u)}</td>
                    <td className="py-3 px-4">{displayLastName(u)}</td>
                    <td className="py-3 px-4">{u.email}</td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {u.number || u.mobile || "—"}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {[u.pan, u.broker].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {[u.experience, u.risk].filter(Boolean).join(" / ") ||
                        "—"}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">
                        {u.role || "user"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {u.createdAt
                        ? new Date(u.createdAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Tooltip title="Edit">
                          <button
                            type="button"
                            onClick={() => handleEdit(u)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <button
                            type="button"
                            onClick={() => handleDelete(u.id)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-danger-600 hover:bg-danger-50 hover:text-danger-700 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
